import { ref } from 'vue'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type {
  GivePortalSelfAssessmentBody,
  PortalSelfAssessment,
  PortalSession,
  PortalSubmitResult,
  PortalTask,
} from '../models/portal-session'
import { httpErrorMessage, httpStatusCode } from './http-status-code'
import { usePortalToken } from './use-portal-token'

const sortTasks = (tasks: readonly PortalTask[]) => {
  return [...tasks].sort((left, right) => left.startedAt.localeCompare(right.startedAt))
}

export const usePortalTasks = () => {
  const { headers, clearSession, hasSession } = usePortalToken()

  const questionnaireEndpoint = ref('portal-session/tasks/_')
  const submitEndpoint = ref('portal-session/tasks/_/self-assessment')
  const {
    data: session,
    status: sessionStatus,
    error: sessionError,
    load: loadSession,
  } = useGet<PortalSession>('portal-session', { omitOrganization: true, headers })
  const {
    data: questionnaire,
    status: questionnaireStatus,
    error: questionnaireError,
    load: loadQuestionnaireRequest,
  } = useGet<PortalSelfAssessment>(questionnaireEndpoint, { omitOrganization: true, headers })
  const {
    status: submitStatus,
    error: submitError,
    mutate: postAssessment,
  } = usePost<void, GivePortalSelfAssessmentBody>(submitEndpoint, true, headers)

  const loadTasks = async (): Promise<
    { ok: true; firstName: string; tasks: PortalTask[] } | { ok: false; reason: 'ended' | 'error' }
  > => {
    if (!hasSession()) {
      return { ok: false, reason: 'ended' }
    }
    await loadSession()
    if (sessionStatus.value === ApiRequestStatus.Success && session.value) {
      return {
        ok: true,
        firstName: session.value.firstName,
        tasks: sortTasks(session.value.tasks),
      }
    }
    if (httpStatusCode(sessionError.value) === 401) {
      clearSession()
      return { ok: false, reason: 'ended' }
    }
    return { ok: false, reason: 'error' }
  }

  const loadQuestionnaire = async (
    surveyId: string,
  ): Promise<
    | { ok: true; questionnaire: PortalSelfAssessment }
    | { ok: false; reason: 'ended' | 'notFound' | 'error' }
  > => {
    if (!hasSession()) {
      return { ok: false, reason: 'ended' }
    }
    questionnaireEndpoint.value = `portal-session/tasks/${encodeURIComponent(surveyId)}`
    await loadQuestionnaireRequest()
    if (questionnaireStatus.value === ApiRequestStatus.Success && questionnaire.value) {
      return { ok: true, questionnaire: questionnaire.value }
    }
    const status = httpStatusCode(questionnaireError.value)
    if (status === 401) {
      clearSession()
      return { ok: false, reason: 'ended' }
    }
    if (status === 404) {
      return { ok: false, reason: 'notFound' }
    }
    return { ok: false, reason: 'error' }
  }

  const submitFailure = (error: unknown): PortalSubmitResult => {
    const status = httpStatusCode(error)
    const message = httpErrorMessage(error)
    if (status === 401) {
      clearSession()
      return { ok: false, reason: 'ended' }
    }
    if (status === 404) {
      return { ok: false, reason: 'notFound' }
    }
    if (status === 409) {
      return { ok: false, reason: 'done' }
    }
    if (status === 400) {
      return { ok: false, reason: 'validation', message: message ?? '' }
    }
    return { ok: false, reason: 'error' }
  }

  const submitSelfAssessment = async (
    surveyId: string,
    body: GivePortalSelfAssessmentBody,
  ): Promise<PortalSubmitResult> => {
    if (!hasSession()) {
      return { ok: false, reason: 'ended' }
    }
    if (submitStatus.value === ApiRequestStatus.Loading) {
      return { ok: false, reason: 'error' }
    }
    submitEndpoint.value = `portal-session/tasks/${encodeURIComponent(surveyId)}/self-assessment`
    await postAssessment(body)
    if (submitStatus.value === ApiRequestStatus.Success) {
      return { ok: true }
    }
    return submitFailure(submitError.value)
  }

  return {
    sessionStatus,
    submitStatus,
    loadTasks,
    loadQuestionnaire,
    submitSelfAssessment,
  }
}
